import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { SlotContent } from '@utils/Slot'
import React, { useEffect, useRef, useState } from 'react'
import JackpotStatisticsItem from '../common/JackpotStatisticsItem'
import { jackpotEQConfig } from './config'
import JackpotEQTooltip from './JackpotEQTooltip'
import { getAmountAfterHideCents } from '@utils/money'
import { withAutoBlur } from '@utils/misc.ts'
import {
    JackpotEQLevels,
    JackpotLevelRomanNumberMap,
    JackpotLevelIndexMap,
    JackpotPhase,
    JackpotType,
} from 'game-ui-types'
import { useBetSliderStore } from '@stores/betSliderStore'
import { isBetOptionItem } from '@utils/betOption'

interface JackpotEQStatisticsItemProps {
    type: JackpotEQLevels
    isTooltipActive: boolean
    isPanelActive: boolean
    onClick: (level: JackpotEQLevels) => void
    onMouseOver?: (level: JackpotEQLevels) => void
    onMouseOut?: () => void
}

/**
 * JackpotEQStatisticsItem Component
 *
 * Renders jackpot EQ tooltip and passes stats data to generic jackpot statistics item.
 * Keep track and dictates/controls how to animate jackpot value for Jackpot EQ.
 * Values are kept in cents and then Odometer will take care of formatting
 */
const JackpotEQStatisticsItem: React.FC<JackpotEQStatisticsItemProps> = ({
    type,
    isTooltipActive,
    isPanelActive,
    onClick,
    onMouseOver,
    onMouseOut,
}) => {
    const jackpotEQStatistics = useJackpotStore((state) => state.jackpotEQStatistics)
    const jackpotPhase = useJackpotStore((state) => state.jackpotPhase)
    const isJackpotRoundRunning = useJackpotStore((state) => state.isJackpotRoundRunning)
    const freezeIndex = useJackpotStore((state) => state.freezeIndex)
    const hideCents = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)
    const odometerMaxLimit = {
        [JackpotEQLevels.BRONZE]: 100,
        [JackpotEQLevels.SILVER]: 100,
        [JackpotEQLevels.GOLD]: 100,
        [JackpotEQLevels.PLATINUM]: 100,
    }

    const isRemoteGame = useConfigurationStore((state) => state.isRemoteGame)
    const betOptions = useConfigurationStore((state) => state.betOptions)
    const shouldHideJackpotWinnerNames = useConfigurationStore((state) => state.shouldHideJackpotWinnerNames)

    const activeIndex = useBetSliderStore((state) => state.activeIndex)
    const activeBetGroupBetOptions = useBetSliderStore((state) => state.activeBetGroupBetOptions)
    const activeBetGroupBetIndex = useBetSliderStore((state) => state.activeBetGroupBetIndex)

    const [amount, setAmount] = useState(-1)
    const realTimeAmountInOdometerRef = useRef(amount)
    const previousAmount = useRef(amount)
    const [isAmountStatic, setIsAmountStatic] = useState(false)
    const [shouldUseOdometer, setShouldUseOdometer] = useState(true)
    const [winningUser, setWinningUser] = useState('')
    const winningUserDate = useRef('')
    const isWinnerBlinking = useRef(false)

    const getWinningUserDateFromStats = () => {
        return isRemoteGame
            ? (jackpotEQStatistics.egyptQuest!.levels[activeBetGroupBetIndex][JackpotLevelIndexMap[type]].lastWin
                  ?.winDate ?? '')
            : jackpotEQStatistics[`lastWinDateLevel${JackpotLevelRomanNumberMap[type]}`]
    }

    const getWinningUserFromStats = () => {
        return isRemoteGame
            ? (jackpotEQStatistics.egyptQuest!.levels[activeBetGroupBetIndex][JackpotLevelIndexMap[type]].lastWin
                  ?.winUser ?? '')
            : jackpotEQStatistics[`lastWinUserLevel${JackpotLevelRomanNumberMap[type]}`]
    }

    const getWinningAmountFromStats = () => {
        return isRemoteGame
            ? (jackpotEQStatistics.egyptQuest!.levels[activeBetGroupBetIndex][JackpotLevelIndexMap[type]].lastWin
                  ?.winAmount ?? -1)
            : jackpotEQStatistics[`lastWinLevel${JackpotLevelRomanNumberMap[type]}`]
    }

    const getWinningUserPresentationValue = () => {
        if (!shouldHideJackpotWinnerNames) {
            return getWinningUserFromStats()
        }

        return getWinningUserDateFromStats() || `${type}-${getWinningAmountFromStats()}`
    }

    const getJackpotAmountFromStats = () => {
        if (isRemoteGame) {
            const { schemaBalances } = jackpotEQStatistics.egyptQuest ?? {}

            const betGroupIndex = activeBetGroupBetOptions?.[activeIndex]?.betGroupIndex ?? 0
            const activeBet = betOptions[activeIndex]
            const isBetGroup = isBetOptionItem(activeBet)

            const schema = isBetGroup
                ? schemaBalances?.[betGroupIndex]
                : schemaBalances?.find((sb) => sb.betOptions.includes(activeBet))

            return schema ? schema.balances[JackpotLevelIndexMap[type]] : -1
        }

        return jackpotEQStatistics[`level${JackpotLevelRomanNumberMap[type]}`]
    }

    const handleWinningUserPresentationComplete = () => {
        isWinnerBlinking.current = false

        if (winningUser !== '') setWinningUser('')
    }

    const handleBlinkingComplete = () => {
        isWinnerBlinking.current = false

        if (freezeIndex !== JackpotLevelIndexMap[type]) {
            setIsAmountStatic(true)
            setShouldUseOdometer(false)
            setAmount(getJackpotAmountFromStats() || 0)
        }
    }

    const handleBetIndexChanged = () => {
        setIsAmountStatic(true)
        setAmount(getJackpotAmountFromStats())
    }

    const handleRealTimeOdometerUpdate = (currentValueInOdometer: number) => {
        // Keep track of what is shown in the panel (since we animate the current value towards target value)
        // This value is always in cents even if hideCents is true
        realTimeAmountInOdometerRef.current = currentValueInOdometer
    }

    useEffect(() => {
        if (isRemoteGame) {
            handleBetIndexChanged()
        }
    }, [activeIndex, activeBetGroupBetOptions])

    // On schema change (bet group), reset winningUserDate
    useEffect(() => {
        if (isRemoteGame) {
            winningUserDate.current = ''
        }
    }, [activeBetGroupBetIndex])

    useEffect(() => {
        // If no jackpot values exit
        if (getJackpotAmountFromStats() < 0) return

        if (winningUserDate.current === '') winningUserDate.current = getWinningUserDateFromStats()

        // Keep track of previous amount to understand next steps
        previousAmount.current = amount

        const isFrozen = freezeIndex === JackpotLevelIndexMap[type]

        // A player can have multiple games open; only this instance's jackpot phase/freeze state identifies a local win.
        const hasANewPlayerWonJackpot = getWinningUserDateFromStats() !== winningUserDate.current

        // If player finished jackpot and has won
        if (isFrozen && (jackpotPhase === JackpotPhase.OUTRO_STARTED || jackpotPhase === JackpotPhase.OUTRO_CLICKED)) {
            if (amount !== 0) {
                setAmount(0)
                setShouldUseOdometer(false)
            }

            // If showing winning user presentation while current player has won, remove presentation to show countdown to 0
            if (winningUser !== '') setWinningUser('')

            winningUserDate.current = getWinningUserDateFromStats()

            // If win animation clicked, skip countdown animation and show 0
            setIsAmountStatic(jackpotPhase === JackpotPhase.OUTRO_CLICKED)
        } else if (!isFrozen && hasANewPlayerWonJackpot) {
            // If a Player has won a jackpot but the panel is not frozen, start blinking animation with winning amount
            winningUserDate.current = getWinningUserDateFromStats()
            setWinningUser(getWinningUserPresentationValue())
            isWinnerBlinking.current = true

            setAmount(getWinningAmountFromStats())
            setIsAmountStatic(true)
        } else if (!winningUser && !isFrozen) {
            // Otherwise default to setting a new target and check animation type
            if (amount !== getJackpotAmountFromStats()) {
                const newAmount = getJackpotAmountFromStats() ?? 0

                setAmount(newAmount)

                const targetAmount = getAmountAfterHideCents(newAmount, hideCents)
                const realTimeAmount = getAmountAfterHideCents(realTimeAmountInOdometerRef.current, hideCents)

                setShouldUseOdometer(Math.abs(targetAmount - realTimeAmount) <= odometerMaxLimit[type])
                // If previous update amount was 0, set the latest value statically, otherwise animate
                setIsAmountStatic(previousAmount.current === 0)
            }
        }
    }, [jackpotEQStatistics, freezeIndex, jackpotPhase])

    const config = jackpotEQConfig[type]

    return (
        <JackpotStatisticsItem
            config={config}
            type={type}
            realTimeAmount={realTimeAmountInOdometerRef.current > -1 ? realTimeAmountInOdometerRef.current : undefined}
            amount={amount}
            isAmountStatic={isAmountStatic}
            shouldUseOdometer={shouldUseOdometer}
            winningUser={winningUser}
            isPanelActive={isPanelActive}
            jackpotType={JackpotType.JACKPOT_EGYPT_QUEST}
            onWinningUserPresentationComplete={handleWinningUserPresentationComplete}
            onBlinkingComplete={handleBlinkingComplete}
            onClick={withAutoBlur(() => onClick(type))}
            onMouseOver={() => onMouseOver?.(type)}
            onMouseOut={onMouseOut}
            onRealTimeOdometerUpdate={handleRealTimeOdometerUpdate}
        >
            <SlotContent slot="above">
                {isJackpotRoundRunning}
                <JackpotEQTooltip level={type} isActive={isTooltipActive} />
            </SlotContent>
        </JackpotStatisticsItem>
    )
}

export default JackpotEQStatisticsItem
